import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { BillingPlansSelectors } from '@core/billing-plans-data';
import { createSingleEntityStore, ngrxEvent } from '@core/ngrx-legacy';
import type { CancelPayload, Subscription } from '@core/subscription-types';

import type {
  AddCardSuccessPayload,
  CancelSuccessPayload,
  ChangePlanPayload,
  ChangePlanSuccessPayload,
  ChangeSlotsSuccessPayload,
  LoadNextPaymentSuccessPayload,
  PatchSubscriptionPayload,
  PaymentIntentPayload,
  PaymentIntentSuccessPayload,
} from './subscription-pricing.types';

const { actions, reducer, namespace, selectors } = createSingleEntityStore<Subscription>()(
  'subscription-pricing',
  {
    customActions: () => ({
      changePlan: ngrxEvent<ChangePlanPayload>(),
      changePlanSuccess: ngrxEvent<ChangePlanSuccessPayload>(),
      changePlanFailure: ngrxEvent<any>(),

      changeSlots: ngrxEvent<number>(),
      changeSlotsSuccess: ngrxEvent<ChangeSlotsSuccessPayload>(),
      changeSlotsFailure: ngrxEvent<any>(),

      cancel: ngrxEvent<CancelPayload>(),
      cancelSuccess: ngrxEvent<CancelSuccessPayload>(),
      cancelFailure: ngrxEvent<any>(),

      reactivate: ngrxEvent<void>(),
      reactivateSuccess: ngrxEvent(),
      reactivateFailure: ngrxEvent<any>(),

      deleteCard: ngrxEvent<void>(),
      deleteCardSuccess: ngrxEvent<boolean>(),
      deleteCardFailure: ngrxEvent<any>(),

      addCard: ngrxEvent<string>(),
      addCardSuccess: ngrxEvent<AddCardSuccessPayload>(),
      addCardFailure: ngrxEvent<{ error: HttpErrorResponse }>(),

      loadNextPayment: ngrxEvent<void>(),
      loadNextPaymentSuccess: ngrxEvent<LoadNextPaymentSuccessPayload>(),
      loadNextPaymentFailure: ngrxEvent<any>(),

      paymentIntent: ngrxEvent<PaymentIntentPayload>(),
      paymentIntentSuccess: ngrxEvent<PaymentIntentSuccessPayload>(),
      paymentIntentFailure: ngrxEvent<any>(),

      paymentIntentStripeFailure: ngrxEvent<{ error: any }>(),

      patchSubscription: ngrxEvent<PatchSubscriptionPayload>(),
      patchSubscriptionSuccess: ngrxEvent<void>(),
      patchSubscriptionFailure: ngrxEvent<{ error: HttpErrorResponse }>(),
    }),
  },
);

const activePlanPrice = createSelector(
  BillingPlansSelectors.activePlan,
  selectors.data,
  (activePlan, subscription) => {
    if (!activePlan || !subscription) return undefined;

    if (subscription.planQuantity) {
      return activePlan.prices.find(
        price =>
          price.billingPeriodMonths === activePlan.billingPeriod &&
          (price.fteRangeEnd === subscription.planQuantity ||
            price.fteRangeStart === subscription.planQuantity),
      );
    } else {
      return activePlan.prices.find(
        price => price.billingPeriodMonths === activePlan.billingPeriod,
      );
    }
  },
);

const SubscriptionPricingSelectors = { ...selectors, activePlanPrice };

export {
  actions as SubscriptionPricingActions,
  reducer as SubscriptionPricingReducer,
  SubscriptionPricingSelectors,
  namespace as SubscriptionPricingNamespace,
};
